/**
 * Banka tanıma sözlüğü: ad → logo, marka rengi ve kısa kod.
 *
 * Banka adı arka uçtan serbest metin geliyor ("Ziraat Bankası", "Yapı ve Kredi
 * Bankası A.Ş.", "T. GARANTİ BANKASI A.Ş." ...). Eşleme bu yüzden birebir ada
 * değil, normalize edilmiş metindeki anahtar kelimelere bakıyor.
 *
 * Logolar uygulamayla birlikte sunuluyor (assets/images/banks) — dış bir logo
 * servisine bağlanmıyoruz. Logosu olmayan bankada marka renkli kısa kod rozeti
 * gösteriliyor; jenerik bir "dünya" ikonu yanlış banka göstermekle eşdeğer bir
 * yanılgı üretirdi.
 *
 * Mobil uygulamadaki `src/shared/constants/banks.ts` ile aynı sözlük: iki
 * istemci aynı hareketi aynı logoyla göstermeli.
 */

export interface BankBrand {
  key: string;
  /** Rozet ve logosuz durumda kullanılan kısa kod. */
  short: string;
  /** Marka rengi — logo yokken rozetin zemini. */
  color: string;
  /** Normalize edilmiş banka adında aranan anahtarlar. */
  match: string[];
  /** assets altındaki logo yolu; yoksa rozete düşülür. */
  logo?: string;
}

const LOGO_PATH = 'assets/images/banks';

/**
 * Türkçe karakterleri ve noktalamayı düşürür; "T. GARANTİ BANKASI A.Ş." ile
 * "Garanti BBVA" aynı eşlemeye düşsün diye.
 */
export function normalizeBankName(name: string | null | undefined): string {
  return (name ?? '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Sıra önemli: "ziraat katilim" metninde "ziraat" de geçiyor; katılım
 * bankaları listenin başında ki mevduat bankasına düşmesinler.
 */
const BANKS: BankBrand[] = [
  { key: 'ziraatkatilim', short: 'ZK', color: '#00674F', match: ['ziraat katilim'], logo: `${LOGO_PATH}/ziraatkatilim.png` },
  { key: 'vakifkatilim', short: 'VK', color: '#1B7A4B', match: ['vakif katilim'], logo: `${LOGO_PATH}/vakifkatilim.png` },
  { key: 'emlakkatilim', short: 'EK', color: '#0C3C60', match: ['emlak katilim'] },
  { key: 'turkiyefinans', short: 'TF', color: '#00A0DF', match: ['turkiye finans'] },
  { key: 'kuveytturk', short: 'KT', color: '#00A24A', match: ['kuveyt turk'], logo: `${LOGO_PATH}/kuveytturk.png` },
  { key: 'albaraka', short: 'AL', color: '#009A44', match: ['albaraka'], logo: `${LOGO_PATH}/albaraka.png` },
  { key: 'ziraat', short: 'ZB', color: '#E4002B', match: ['ziraat'], logo: `${LOGO_PATH}/ziraat.png` },
  { key: 'isbank', short: 'İŞ', color: '#003C7E', match: ['is bankasi', 'isbank', 'turkiye is'], logo: `${LOGO_PATH}/isbank.png` },
  { key: 'garanti', short: 'GB', color: '#00A94F', match: ['garanti'], logo: `${LOGO_PATH}/garanti.png` },
  { key: 'yapikredi', short: 'YK', color: '#00437B', match: ['yapi kredi', 'yapi ve kredi', 'yapikredi'], logo: `${LOGO_PATH}/yapikredi.png` },
  { key: 'akbank', short: 'AK', color: '#E2001A', match: ['akbank'], logo: `${LOGO_PATH}/akbank.png` },
  { key: 'qnb', short: 'QNB', color: '#67227C', match: ['qnb', 'finansbank', 'enpara'], logo: `${LOGO_PATH}/qnb.png` },
  { key: 'halkbank', short: 'HB', color: '#005BAA', match: ['halk bankasi', 'halkbank'], logo: `${LOGO_PATH}/halkbank.png` },
  { key: 'vakifbank', short: 'VB', color: '#F9C606', match: ['vakiflar bankasi', 'vakifbank', 'vakif'], logo: `${LOGO_PATH}/vakifbank.png` },
  { key: 'denizbank', short: 'DB', color: '#0072CE', match: ['denizbank'] },
  { key: 'teb', short: 'TEB', color: '#00953B', match: ['teb', 'ekonomi bankasi'], logo: `${LOGO_PATH}/teb.png` },
  { key: 'ing', short: 'ING', color: '#FF6200', match: ['ing'], logo: `${LOGO_PATH}/ing.png` },
  { key: 'sekerbank', short: 'ŞB', color: '#E30613', match: ['sekerbank'], logo: `${LOGO_PATH}/sekerbank.png` },
  { key: 'odeabank', short: 'OD', color: '#E4002B', match: ['odeabank', 'odea'], logo: `${LOGO_PATH}/odeabank.png` },
  { key: 'fibabanka', short: 'FB', color: '#8C1D40', match: ['fibabanka', 'fiba'], logo: `${LOGO_PATH}/fibabanka.png` },
  { key: 'alternatifbank', short: 'AB', color: '#E4002B', match: ['alternatif'], logo: `${LOGO_PATH}/alternatifbank.png` },
  { key: 'burganbank', short: 'BB', color: '#00529B', match: ['burgan'], logo: `${LOGO_PATH}/burganbank.png` },
  { key: 'anadolubank', short: 'AN', color: '#00519E', match: ['anadolubank', 'anadolu bank'], logo: `${LOGO_PATH}/anadolubank.png` },
  { key: 'hsbc', short: 'HS', color: '#DB0011', match: ['hsbc'], logo: `${LOGO_PATH}/hsbc.png` },
  { key: 'aktifbank', short: 'AT', color: '#009EE0', match: ['aktif yatirim', 'aktifbank'], logo: `${LOGO_PATH}/aktifbank.png` },
];

/** Tanınmayan banka: adın baş harfleri ve nötr renk. */
function fallbackBrand(name: string | null | undefined): BankBrand {
  const words = normalizeBankName(name).split(' ').filter(w => w);
  const short = words
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toLocaleUpperCase('tr-TR');

  return { key: 'unknown', short: short || '?', color: '#74788d', match: [] };
}

export function resolveBankBrand(name: string | null | undefined): BankBrand {
  const normalized = normalizeBankName(name);
  if (!normalized) return fallbackBrand(name);

  return BANKS.find(bank => bank.match.some(m => normalized.indexOf(m) > -1)) ?? fallbackBrand(name);
}
